import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  Building2,
  Clock,
  Flame,
  Home,
  Landmark,
  MapPin,
  UtensilsCrossed,
  Users,
  Car,
  Droplets,
} from 'lucide-react'

import { CityCatalogItem } from '../../services/searchDefaults'

const asset = (name: string) => `${import.meta.env.BASE_URL}manauna-dham/${name}`

const isManauna = (city?: string) => {
  const c = (city || '').toLowerCase()
  return c.includes('manauna') || c.includes('manona') || c.includes('aonla')
}

type StayCard = {
  id: string
  title: string
  tagline: string
  badge: string
  fomo: string
  priceHint: string
  icon: typeof Home
  link: string
  accent: string
}

const HERO_BG =
  'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?auto=format&fit=crop&w=2400&q=90'

const STAY_CARDS: StayCard[] = [
  {
    id: 'hotels',
    title: 'Hotels near Manauna Dham',
    tagline: 'AC rooms, attached baths, family suites—walkable to darshan routes with easy check-in.',
    badge: 'Most booked this season',
    fomo: '18 hotel rooms booked this week',
    priceHint: 'From ₹1,200/night',
    icon: Building2,
    link: '/rooms?location=Manauna',
    accent: 'from-primary-400/30 via-amber-500/20 to-primary-700/15',
  },
  {
    id: 'homestay',
    title: 'BnBs & Budget Homestays',
    tagline: 'Wallet-friendly stays for small groups and longer yatras when hotels are full.',
    badge: 'Budget option',
    fomo: '12 families booked this week',
    priceHint: 'From ₹799/night',
    icon: Home,
    link: '/rooms?location=Manauna',
    accent: 'from-amber-400/20 via-primary-500/15 to-orange-600/10',
  },
  {
    id: 'food',
    title: 'Prasad & Home-style Meals',
    tagline: 'Satvik kitchens, thali partners, and local food guidance for yatris.',
    badge: 'Devotee-friendly menus',
    fomo: '47 meal orders this month',
    priceHint: 'Meals from ₹120',
    icon: UtensilsCrossed,
    link: '/manauna-dham/food',
    accent: 'from-orange-400/20 via-primary-500/15 to-amber-600/10',
  },
  {
    id: 'transport',
    title: 'Cab, Auto & Yatra Routes',
    tagline: 'Bareilly → Aonla → Manauna village—reach the Dham without the stress.',
    badge: 'Door-to-darshan help',
    fomo: '18 rides scheduled today',
    priceHint: 'Shared from ₹350',
    icon: Car,
    link: '/manauna-dham/transport',
    accent: 'from-sky-400/15 via-primary-400/15 to-primary-600/10',
  },
]

const FOMO_LINES = [
  '2 hotel rooms near Manauna Dham were booked in the last hour',
  'Weekend hotel slots down to single digits',
  'Darshan season: hotel prices rising—book early',
  '127 devotees compared hotels near the Dham today',
  'AC hotel rooms with parking filling fast for this weekend',
]

function useFomoTicker(intervalMs = 4200) {
  const [index, setIndex] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % FOMO_LINES.length)
    }, intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs])
  return FOMO_LINES[index]
}

function GlassOrb({ className, delay = '0s' }: { className: string; delay?: string }) {
  return (
    <div
      className={`manauna-glass-orb pointer-events-none absolute rounded-full ${className}`}
      style={{ animationDelay: delay }}
      aria-hidden
    />
  )
}

export default function ManaunaSplashSection({ city }: { city?: CityCatalogItem | null }) {
  const fomoLine = useFomoTicker()
  const [mounted, setMounted] = useState(false)
  const manauna = isManauna(city?.city)
  const cityLabel = manauna ? 'Manauna Dham' : city?.city || 'your city'
  const roomsLink = `/rooms?location=${encodeURIComponent(city?.location || city?.city || 'Manauna')}`

  useEffect(() => {
    const t = window.setTimeout(() => setMounted(true), 80)
    return () => window.clearTimeout(t)
  }, [])

  const liveStats = useMemo(
    () => ({
      viewing: 18 + Math.floor(Math.random() * 14),
      left: 4 + Math.floor(Math.random() * 5),
    }),
    []
  )

  return (
    <section className="relative min-h-[88vh] overflow-hidden bg-secondary-950 text-white">
      {/* Background — HD hotel imagery + strong readable scrim */}
      <div
        className="absolute inset-0 bg-cover bg-center manauna-parallax-bg"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="absolute inset-0 bg-secondary-950/55" aria-hidden />
      <div className="absolute inset-0 manauna-hero-scrim" aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-t from-secondary-950 via-transparent to-secondary-950/40" aria-hidden />

      <GlassOrb className="-top-20 -left-16 h-56 w-56 opacity-40" />
      <GlassOrb className="top-1/3 -right-24 h-72 w-72 opacity-30" delay="1.2s" />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-16">
        {/* FOMO ticker */}
        <div
          className={`mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 transition-all duration-1000 ${
            mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="manauna-glass-readable inline-flex items-center gap-2.5 rounded-full px-4 py-2 text-xs sm:text-sm max-w-full">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
            </span>
            <span className="font-medium text-white truncate">{fomoLine}</span>
          </div>
          <div className="manauna-glass-readable flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] sm:text-xs text-white/95 shrink-0">
            <Users className="h-3.5 w-3.5 text-primary-300" />
            <span>
              <strong className="text-primary-200">{liveStats.viewing}</strong> comparing hotels now
            </span>
            <span className="hidden sm:inline text-white/40">|</span>
            <Flame className="hidden sm:block h-3.5 w-3.5 text-orange-400" />
            <span className="hidden sm:inline">
              Only <strong className="text-orange-200">{liveStats.left}</strong> hotel rooms left this week
            </span>
          </div>
        </div>

        {/* Hero headline */}
        <div
          className={`grid lg:grid-cols-12 gap-8 lg:gap-10 items-start transition-all duration-1000 delay-150 ${
            mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="lg:col-span-7 manauna-hero-panel rounded-3xl p-6 sm:p-8 lg:p-9">
            <div className="manauna-glass-readable mb-5 inline-flex items-center gap-3 rounded-2xl pl-2 pr-5 py-2">
              <img
                src={asset('logonobg.webp')}
                alt=""
                className="h-11 w-11 rounded-xl bg-white/10 p-1.5 object-contain"
                onError={(e) => {
                  ;(e.target as HTMLImageElement).style.display = 'none'
                }}
              />
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-primary-300 font-semibold">
                  {manauna ? 'Hotels · Manauna Dham' : `Stays · ${cityLabel}`}
                </p>
                <p className="text-sm font-bold text-white">{city?.hero?.region || 'Aonla Stay'}</p>
              </div>
            </div>

            <h2 className="manauna-hero-title text-3xl sm:text-4xl md:text-5xl lg:text-[3.1rem] font-bold leading-[1.12] tracking-tight text-white">
              Book trusted{' '}
              <span className="text-primary-300">
                {manauna ? 'hotels & comfort stays' : `rooms in ${cityLabel}`}
              </span>{' '}
              {manauna ? 'minutes from Manauna Dham' : 'with verified hosts'}
            </h2>
            <p className="mt-4 max-w-2xl text-base sm:text-lg text-white/90 leading-relaxed">
              {manauna
                ? "AC rooms, family suites, dharmshalas, and verified listings near darshan routes—plus satvik food and yatra support for Shri Khatu Shyam Ji's devotees visiting Aonla."
                : `${city?.room_count || 'Verified'} listings in ${cityLabel}. Homestays, hotels, and guest houses with secure online booking and WhatsApp confirmations.`}
            </p>

            <div className="mt-6 flex flex-wrap gap-2.5">
              {['Hotels & lodges', 'Near darshan', 'AC rooms', 'Easy check-in'].map((chip) => (
                <span
                  key={chip}
                  className="manauna-glass-chip rounded-full px-3.5 py-1.5 text-xs font-semibold text-white/95"
                >
                  {chip}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/rooms?location=Manauna"
                className="group manauna-cta-primary inline-flex items-center justify-center gap-2 rounded-2xl px-7 py-4 text-sm font-bold text-secondary-950 shadow-2xl shadow-black/40 transition-transform active:scale-[0.98]"
              >
                <Building2 className="h-4 w-4" />
                Browse hotels near the Dham
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/manauna-dham/accommodations"
                className="manauna-glass-readable inline-flex items-center justify-center gap-2 rounded-2xl px-7 py-4 text-sm font-semibold text-white hover:bg-white/15 transition-colors"
              >
                <Landmark className="h-4 w-4 text-primary-300" />
                All stay options
              </Link>
            </div>
          </div>

          {/* Timing card */}
          <div className="lg:col-span-5">
            <div className="manauna-glass-panel manauna-hero-panel relative overflow-hidden rounded-3xl p-6 sm:p-7">
              <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-primary-500/20 blur-3xl" />
              <div className="relative">
                <div className="flex items-start justify-between gap-3 mb-5">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-primary-300 font-bold">Don&apos;t miss</p>
                    <h3 className="text-xl font-bold mt-1 text-white">Darshan season is live</h3>
                  </div>
                  <span className="premium-badge shrink-0 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-secondary-900">
                    Limited
                  </span>
                </div>

                <div className="space-y-3.5">
                  <div className="flex items-center gap-3 rounded-2xl bg-white/8 border border-white/12 px-4 py-3">
                    <Clock className="h-5 w-5 text-primary-300 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-white">Darshan 7 AM – 9 PM</p>
                      <p className="text-xs text-white/75">Aarti ~8 AM & 5 PM · confirm on arrival</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-2xl bg-white/8 border border-white/12 px-4 py-3">
                    <MapPin className="h-5 w-5 text-primary-300 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-white">Manauna village, Aonla</p>
                      <p className="text-xs text-white/75">Bareilly district, UP – 243301</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-2xl bg-amber-500/15 border border-amber-400/30 px-4 py-3">
                    <Flame className="h-5 w-5 text-amber-300 shrink-0" />
                    <p className="text-sm text-white/95">
                      Hotels near the Dham are{' '}
                      <strong className="text-amber-200">booking 3× faster</strong> this month—secure a room early.
                    </p>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-2">
                  <Link
                    to="/manauna-dham/shyam-jal"
                    className="flex items-center gap-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2.5 text-xs font-semibold transition-colors"
                  >
                    <Droplets className="h-4 w-4 text-sky-300" />
                    Shyam Jal
                  </Link>
                  <Link
                    to="/manauna-dham/patient-token"
                    className="flex items-center gap-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2.5 text-xs font-semibold transition-colors"
                  >
                    <Landmark className="h-4 w-4 text-primary-300" />
                    Patient token
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stay variety — glass cards */}
        <div
          className={`mt-12 sm:mt-16 transition-all duration-1000 delay-300 ${
            mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-primary-300 font-bold">Near Manauna Dham</p>
              <h3 className="text-2xl sm:text-3xl font-bold mt-1 text-white manauna-hero-title">
                Hotels first—food & yatra support included
              </h3>
            </div>
            <p className="text-sm text-white/80 max-w-md">
              Compare verified hotels and lodges, then add homestays, meals, or transport for your yatra.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5">
            {STAY_CARDS.map((card, i) => {
              const Icon = card.icon
              const isFeatured = card.id === 'hotels'
              return (
                <Link
                  key={card.id}
                  to={card.link}
                  className={`manauna-stay-card group relative overflow-hidden rounded-3xl ${
                    isFeatured ? 'manauna-stay-card--featured sm:col-span-2 xl:col-span-1 ring-2 ring-primary-400/50' : ''
                  }`}
                  style={{ animationDelay: `${400 + i * 100}ms` }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${card.accent} opacity-90`} />
                  <div className="manauna-glass-panel relative m-[1px] rounded-[calc(1.5rem-1px)] p-5 h-full flex flex-col min-h-[220px]">
                    <div className="flex items-start justify-between gap-2 mb-4">
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-500/25 border border-primary-400/35">
                        <Icon className="h-5 w-5 text-primary-200" />
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-primary-100 bg-primary-500/20 border border-primary-400/30 rounded-full px-2.5 py-1">
                        {card.badge}
                      </span>
                    </div>
                    <h4 className="text-lg font-bold leading-snug text-white">{card.title}</h4>
                    <p className="mt-2 text-sm text-white/85 leading-relaxed flex-1">{card.tagline}</p>
                    <div className="mt-4 pt-4 border-t border-white/15 flex items-center justify-between gap-2">
                      <div>
                        <p className="text-xs text-orange-200 font-semibold">{card.fomo}</p>
                        <p className="text-sm font-bold text-primary-100 mt-0.5">{card.priceHint}</p>
                      </div>
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 group-hover:bg-primary-500 group-hover:text-secondary-950 transition-colors">
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>

        {/* Bottom urgency strip */}
        <div
          className={`mt-10 manauna-glass-readable rounded-2xl px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 transition-all duration-1000 delay-500 ${
            mounted ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <p className="text-sm text-white/95">
            <strong className="text-white">Hotel rooms go fast in darshan season.</strong> Book now or settle for
            stays farther from the Dham.
          </p>
          <Link
            to="/rooms?location=Manauna"
            className="shrink-0 text-sm font-bold text-primary-200 hover:text-primary-100 inline-flex items-center gap-1.5"
          >
            Find hotels near Manauna
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  )
}
